import { Button, Group, Stack, Text } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { SYNC_STATUS } from '../../domain/status.js';
import type { RowFilter, RowSegment } from '../../domain/rowFilter.js';
import { DebouncedSearchInput } from './DebouncedSearchInput.js';

/**
 * The phone's toolbar above the cards: search, then All / Needs attention. No status select, no
 * legend. A status or environment that came in with the URL has no control here, so it shows as
 * one removable chip — a phone never filters by something it cannot see or clear.
 */
export const MobileFilters = ({
    value,
    onChange,
    placeholder,
}: {
    value: RowFilter;
    onChange: (patch: Partial<RowFilter>) => void;
    placeholder: string;
}) => (
    <Stack gap="xs">
        <DebouncedSearchInput
            aria-label="Search"
            placeholder={placeholder}
            value={value.search}
            onChange={(search) => onChange({ search })}
        />
        <Group gap={6} wrap="wrap">
            {(['all', 'needs_attention'] as RowSegment[]).map((segment) => (
                <Button
                    key={segment}
                    size="compact-sm"
                    variant={value.segment === segment ? 'light' : 'subtle'}
                    color={value.segment === segment ? 'rgsOrange' : 'gray'}
                    onClick={() => onChange({ segment })}
                >
                    {segment === 'all' ? 'All' : 'Needs attention'}
                </Button>
            ))}
            {(value.status || value.environment) && (
                <Button
                    size="compact-sm"
                    variant="light"
                    color="rgsOrange"
                    rightSection={<IconX size={12} stroke={2} />}
                    onClick={() => onChange({ status: null, environment: null })}
                    aria-label="Clear status and environment"
                >
                    {value.status ? SYNC_STATUS[value.status].label : 'Any status'}
                    {value.environment && (
                        <>
                            {' in '}
                            <Text span inherit className="rgs-numeric">
                                {value.environment}
                            </Text>
                        </>
                    )}
                </Button>
            )}
        </Group>
    </Stack>
);
